import { useState } from 'react';
import { findJob } from '@/mocks/jobsData';

interface Props {
  jobs: string[];
}

const rankIcons = [
  'ri-medal-line',
  'ri-award-line',
  'ri-star-smile-line',
  'ri-lightbulb-flash-line',
  'ri-rocket-line',
  'ri-compass-line',
];

export default function ResultJobs({ jobs }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  if (jobs.length === 0) return null;

  const topJobs = jobs.slice(0, 6);
  const restJobs = jobs.slice(6);

  return (
    <div className="bg-background-50 border border-accent-200 rounded-xl p-5 mb-4">
      <h2 className="font-semibold text-foreground-950 mb-1 flex items-center gap-2">
        <i className="ri-briefcase-4-line text-accent-500"></i>
        추천 직업 · 진로
      </h2>
      <p className="text-xs text-foreground-500 mb-4">
        나에게 잘 맞는 순서로 정리했어요. 카드를 누르면 연봉·은퇴연령·상세 정보를 볼 수 있어요.
      </p>

      <div className="space-y-2.5 mb-3">
        {topJobs.map((job, idx) => {
          const info = findJob(job);
          const isOpen = expanded === job;
          return (
            <div
              key={job}
              className={`border rounded-xl transition-colors ${
                isOpen ? 'border-accent-400 bg-accent-50/50' : 'border-background-200 bg-background-50'
              }`}
            >
              <button
                onClick={() => setExpanded(isOpen ? null : job)}
                className="whitespace-normal w-full text-left flex items-start gap-3 p-3.5 cursor-pointer"
              >
                <div
                  className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    idx === 0 ? 'bg-accent-500 text-white' : 'bg-accent-100 text-accent-600'
                  }`}
                >
                  <i className={rankIcons[idx % rankIcons.length]}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-0.5">
                    <span className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{job}</span>
                    {info && (
                      <span className="text-[10px] bg-secondary-100 text-secondary-700 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        {info.category}
                      </span>
                    )}
                    {idx === 0 && (
                      <span className="text-[10px] bg-accent-500 text-white px-1.5 py-0.5 rounded-full whitespace-nowrap">
                        나에게 가장 잘 맞는 직업
                      </span>
                    )}
                  </div>

                  {/* 요약 + 연봉·은퇴연령 */}
                  {info && (
                    <>
                      <p className="text-xs text-foreground-600 leading-relaxed">{info.summary}</p>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        <span className="text-[11px] bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-wallet-3-line"></i>{info.salary}
                        </span>
                        <span className="text-[11px] bg-primary-100 text-primary-800 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <i className="ri-user-star-line"></i>은퇴 {info.retirementAge}
                        </span>
                      </div>
                    </>
                  )}
                </div>
                <i
                  className={`ri-arrow-down-s-line text-foreground-400 flex-shrink-0 mt-1 transition-transform ${
                    isOpen ? 'rotate-180 text-accent-500' : ''
                  }`}
                ></i>
              </button>

              {/* 상세 정보 */}
              {isOpen && info && (
                <div className="px-3.5 pb-3.5 pt-0 pl-16">
                  <div className="border-t border-accent-200 pt-3 space-y-3">
                    <p className="text-xs text-foreground-700 leading-relaxed">{info.description}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <div className="bg-background-50 border border-background-200 rounded-lg p-2.5">
                        <div className="text-[10px] text-foreground-500 mb-1 flex items-center gap-1">
                          <i className="ri-graduation-cap-line"></i>필요 학력·자격
                        </div>
                        <div className="text-xs text-foreground-800">{info.education}</div>
                      </div>
                      <div className="bg-background-50 border border-background-200 rounded-lg p-2.5">
                        <div className="text-[10px] text-foreground-500 mb-1 flex items-center gap-1">
                          <i className="ri-user-heart-line"></i>잘 맞는 성향
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {info.traits.map((trait) => (
                            <span
                              key={trait}
                              className="text-[10px] bg-secondary-50 text-secondary-700 border border-secondary-200 px-1.5 py-0.5 rounded-full whitespace-nowrap"
                            >
                              {trait}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {restJobs.length > 0 && (
        <div>
          <div className="text-xs text-foreground-500 mb-2">그 외 연계 직업</div>
          <div className="flex flex-wrap gap-1.5">
            {restJobs.map((job) => (
              <span
                key={job}
                className="text-xs bg-accent-50 text-accent-700 border border-accent-200 px-2.5 py-1 rounded-full"
              >
                {job}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}